/**
 * Component to display a hierarchy of nested standards within a subtree as a
 * list of cards with trees of standards and related content.
 */
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import Link from "@khanacademy/wonder-blocks-link";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    HeadingLarge,
    HeadingMedium,
    HeadingSmall,
    LabelMedium,
} from "@khanacademy/wonder-blocks-typography";
import {updateHash} from "@khan/urls";

import {CopyToClipboardButton} from "./copy-to-clipboard-button.tsx";
import CoverageBadge from "./coverage-badge.tsx";
import {LevelContext} from "./level-context.tsx";
import type {Coverage} from "./types.ts";

import linkIcon from "@phosphor-icons/core/regular/link.svg";

type Props = {
    title: string;
    description: string;
    coverage?: Coverage | null;
    depth: number;
    isLeaf: boolean;
    layout: "mobile" | "desktop";
    lastClicked: string;
    setLastClicked: (lastClicked: string) => void;
};

function TitleForDepth({
    title,
    description,
    coverage,
    depth,
    isLeaf,
    layout,
    lastClicked,
    setLastClicked,
}: Props): React.ReactElement {
    const level = React.useContext(LevelContext);
    const htmlStrippedDescription = unsafelyStripHTML(description);
    const [showCopyButton, setShowCopyButton] = React.useState(false);
    const copyIconColor = () => {
        // Only show the copy button with a visible color on mobile, when hovering, or when keyboard focused.
        if (layout === "mobile" || showCopyButton) {
            // Render the last clicked copy button with a blue icon.
            return title && title === lastClicked
                ? color.blue
                : color.offBlack64;
        }
        return color.white; // Render the icon with the background color to "hide" it while supporting keyboard navigation.
    };

    const tag = level <= 6 ? "h" + level : "h6"; // Fallback to h6 for any levels deeper than 6.

    if (isLeaf) {
        // Leaf title display -- includes border for related content
        return (
            <View
                style={styles.leafOuterWrapper}
                onMouseEnter={() => setShowCopyButton(true)}
                onMouseLeave={() => setShowCopyButton(false)}
                onFocus={() => setShowCopyButton(true)}
                onBlur={() => setShowCopyButton(false)}
            >
                <View style={styles.flexWrapper}>
                    {
                        <CopyToClipboardButton
                            style={styles.copySlugToClipboard}
                            clipboardText={() =>
                                handleSetTitle(setLastClicked, title)
                            }
                            icon={linkIcon}
                            iconColor={copyIconColor()}
                        />
                    }
                    <HeadingSmall id={title} tag={tag}>
                        <Link style={styles.link} href={`#${title}`}>
                            {title}
                        </Link>
                    </HeadingSmall>
                    {coverage && <CoverageBadge coverage={coverage} />}
                </View>
                <LabelMedium style={styles.leafDescription}>
                    {htmlStrippedDescription}
                </LabelMedium>
            </View>
        );
    }

    if (depth === 0) {
        // Top-level nested title display
        return (
            <View
                style={styles.flexWrapperWithTopMargin}
                onMouseEnter={() => setShowCopyButton(true)}
                onMouseLeave={() => setShowCopyButton(false)}
                onFocus={() => setShowCopyButton(true)}
                onBlur={() => setShowCopyButton(false)}
            >
                {
                    <CopyToClipboardButton
                        style={styles.copySlugToClipboardWithTopMargin}
                        clipboardText={() =>
                            handleSetTitle(setLastClicked, title)
                        }
                        icon={linkIcon}
                        iconColor={copyIconColor()}
                    />
                }
                <Link style={styles.link} href={`#${title}`} tabIndex={0}>
                    <View style={styles.linkWrapper}>
                        <HeadingLarge id={title} tag={tag}>
                            {title}
                        </HeadingLarge>
                        <HeadingLarge tag={tag}>
                            {htmlStrippedDescription}
                        </HeadingLarge>
                    </View>
                </Link>
            </View>
        );
    }
    if (depth === 1) {
        // Non-leaf below top-level nested title display (depth 1)
        return (
            <View
                style={styles.flexWrapper}
                onMouseEnter={() => setShowCopyButton(true)}
                onMouseLeave={() => setShowCopyButton(false)}
                onFocus={() => setShowCopyButton(true)}
                onBlur={() => setShowCopyButton(false)}
            >
                {
                    <CopyToClipboardButton
                        style={styles.copySlugToClipboard}
                        clipboardText={() =>
                            handleSetTitle(setLastClicked, title)
                        }
                        icon={linkIcon}
                        iconColor={copyIconColor()}
                    />
                }
                <Link style={styles.link} href={`#${title}`}>
                    <View style={styles.linkWrapper}>
                        <HeadingMedium id={title} tag={tag}>
                            {title}
                        </HeadingMedium>
                        <HeadingMedium tag={tag}>
                            {htmlStrippedDescription}
                        </HeadingMedium>
                    </View>
                </Link>
            </View>
        );
    }
    // Non-leaf below top-level nested title display (depth 2+)
    return (
        <View
            style={styles.flexWrapper}
            onMouseEnter={() => setShowCopyButton(true)}
            onMouseLeave={() => setShowCopyButton(false)}
            onFocus={() => setShowCopyButton(true)}
            onBlur={() => setShowCopyButton(false)}
        >
            {
                <CopyToClipboardButton
                    style={styles.copySlugToClipboard}
                    clipboardText={() => handleSetTitle(setLastClicked, title)}
                    icon={linkIcon}
                    iconColor={copyIconColor()}
                />
            }
            <Link style={styles.link} href={`#${title}`}>
                <View style={styles.linkWrapper}>
                    <HeadingSmall id={title} tag={tag}>
                        {title}
                    </HeadingSmall>
                    <HeadingSmall tag={tag}>
                        {htmlStrippedDescription}
                    </HeadingSmall>
                </View>
            </Link>
        </View>
    );
}

function handleSetTitle(
    setLastClicked: (title: string) => void,
    title: string,
) {
    setLastClicked(title);
    return updateHash(title);
}

// This is a display hack to strip HTML tags from the EdGate-sourced descriptions ("unsafely" as this technique does *not* prevent XSS).
// Ideally we'd clean up the source data at import-time and/or handle this server-side, but we can tackle that in the future.
function unsafelyStripHTML(description: string) {
    const doc = new DOMParser().parseFromString(description, "text/html");
    return doc.body?.textContent || "";
}

const styles = StyleSheet.create({
    leafOuterWrapper: {
        display: "inline-block",
        marginLeft: spacing.small_12,
        marginBottom: spacing.xxSmall_6,
    },

    flexWrapper: {
        display: "flex",
        flexDirection: "row",
        gap: spacing.xSmall_8,
        marginLeft: spacing.small_12,
        marginBottom: spacing.xxSmall_6,
    },

    flexWrapperWithTopMargin: {
        display: "flex",
        flexDirection: "row",
        gap: spacing.xSmall_8,
        marginTop: spacing.xSmall_8,
        marginLeft: spacing.small_12,
        marginBottom: spacing.xxSmall_6,
    },

    copySlugToClipboard: {
        marginRight: -spacing.xSmall_8,
        marginLeft: -spacing.large_24,
        maxHeight: spacing.large_24,
    },

    copySlugToClipboardWithTopMargin: {
        marginTop: spacing.xSmall_8,
        marginRight: -spacing.xSmall_8,
        marginLeft: -spacing.large_24,
        maxHeight: spacing.large_24,
    },

    leafDescription: {
        borderBottom: `1px solid ${color.offBlack16}`,
        marginBottom: spacing.small_12,
        marginLeft: spacing.small_12,
        paddingBottom: spacing.small_12,
    },

    link: {
        color: color.offBlack,
    },

    linkWrapper: {
        display: "flex",
        flexDirection: "row",
        gap: spacing.xSmall_8,
    },
});

export default TitleForDepth;
